import { AxiosResponse } from "axios";

import {
  IJupyterRegistration,
  IJupyterRequeuePatientsResult,
  IJupyterRequeueResult,
  IJupyterSyncPatientResult,
  JupyterMessageChannel,
} from "../../@types/admin/AdminJupyterRegistration";
import { IPaginatedResponse } from "../../@types/PaginatedResponse";
import { api } from "../api";

/**
 * JupyterHealth Exchange admin page (EN-5149), migrated from the Retool
 * `page_JupyterIntegration` app. One query backs the whole page: the
 * registrations list carries every scope, survey and outbox message inline,
 * so the drill-downs never hit the network.
 */
export const jupyterService = {
  /**
   * Every JHE connection in the caller's region. Page/PageSize are optional
   * on the API and deliberately omitted — Retool fetched the lot and searched
   * client-side, and the connection count is catalogue-sized.
   */
  async getRegistrations(): Promise<
    AxiosResponse<IPaginatedResponse<IJupyterRegistration[]>>
  > {
    return api.get<IPaginatedResponse<IJupyterRegistration[]>>(
      "/Retool/JupyterHealthExchange/Registrations"
    );
  },

  /**
   * Runs the full JHE sync for one patient right now — ingest new vitals and
   * surveys, then drain every due outbox row — instead of waiting for the
   * ward's daily 08:00 job. 404 when the patient has no active integration
   * in this region.
   */
  async syncPatient(
    patientId: number
  ): Promise<AxiosResponse<IJupyterSyncPatientResult>> {
    return api.post<IJupyterSyncPatientResult>(
      `/Retool/JupyterHealthExchange/Patients/${patientId}/Sync`
    );
  },

  /**
   * Puts a Failed or PermanentlyFailed outbox message back on the sync queue
   * with a fresh retry budget (EN-5231 follow-up). The next sync run for the
   * patient's ward delivers it.
   */
  async requeueMessage(
    channel: JupyterMessageChannel,
    messageId: string
  ): Promise<AxiosResponse<IJupyterRequeueResult>> {
    return api.post<IJupyterRequeueResult>(
      `/Retool/JupyterHealthExchange/Messages/${channel}/${messageId}/Requeue`
    );
  },

  /**
   * Requeues every Failed or PermanentlyFailed message (OMH and FHIR) for the
   * given patients in one call — a single patient is a one-element list. The
   * API caps a request at 500 patients.
   */
  async requeuePatients(
    patientIds: number[]
  ): Promise<AxiosResponse<IJupyterRequeuePatientsResult>> {
    return api.post<IJupyterRequeuePatientsResult>(
      "/Retool/JupyterHealthExchange/Messages/Requeue",
      { patientIds }
    );
  },
};
